import { Button } from "@mantine/core";
import cx from "clsx";
import { type FC, useEffect, useState } from "react";

import styles from "./TrainingEvaluationInterstitial.module.css";

const STEPS = ["Transcribing the call", "Preparing your feedback"] as const;

interface Props {
    hasError?: boolean;
    onRetry?: () => void;
}

/**
 * Full-screen "Call complete" interstitial shown between the end of an ASCEND simulation and the
 * arrival of scored results. Progress steps advance on timers to convey perceived progress; the
 * final step intentionally holds until real results arrive and the parent unmounts this component.
 * Ported from `interim-evaluation-screen.mockup.html`.
 */
export const TrainingEvaluationInterstitial: FC<Props> = ({ hasError, onRetry }) => {
    // `currentStep` is the index of the active step: steps before it are "done", the step itself is
    // "active". The last step never auto-completes — it stays active until this component unmounts.
    const [currentStep, setCurrentStep] = useState(0);

    useEffect(() => {
        const timeouts = [setTimeout(() => setCurrentStep(1), 4500)];

        return () => {
            timeouts.forEach(clearTimeout);
        };
    }, []);

    return (
        <div className={styles.overlay} data-testid="evaluation-interstitial" role="status" aria-live="polite">
            <div className={styles.interim}>
                <div className={styles.ringWrap} aria-hidden="true">
                    <div className={styles.ringHalo} />
                    <svg width="132" height="132" viewBox="0 0 132 132">
                        <defs>
                            <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#8223d2" />
                                <stop offset="100%" stopColor="#a45be8" />
                            </linearGradient>
                        </defs>
                        <circle className={styles.ringTrack} cx="66" cy="66" r="60" />
                        <circle className={styles.ringFill} cx="66" cy="66" r="60" />
                        <circle className={styles.ringArc} cx="66" cy="66" r="60" />
                        <circle className={styles.ringArc2} cx="66" cy="66" r="60" />
                    </svg>
                    <div className={styles.ringGlyph}>
                        <div className={styles.wave}>
                            <span />
                            <span />
                            <span />
                            <span />
                            <span />
                        </div>
                    </div>
                </div>

                <h1 className={styles.headline}>Call complete</h1>
                <p className={styles.reassure}>
                    Your simulation ended <b>automatically</b> when the scenario wrapped up — exactly as it&apos;s meant
                    to. <b>Nice work getting all the way through it.</b>
                </p>

                {hasError ? (
                    <div className={cx(styles.steps, styles.errorSteps)}>
                        <p className={styles.errorMessage}>
                            There was an issue loading your results. Clicking below will re-evaluate your session and
                            retrieve your score. This will not restart your simulation.
                        </p>
                        <Button data-testid="retry-evaluation-button" onClick={onRetry} mt="md">
                            Retry
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className={styles.steps} aria-hidden="true">
                            <div className={styles.stepsHead}>
                                <span className={styles.stepsHeadTitle}>Scoring your conversation</span>
                                <span className={styles.stepsHeadEta}>about 20 seconds</span>
                            </div>
                            {STEPS.map((label, index) => (
                                <div
                                    key={label}
                                    className={cx(styles.step, {
                                        [styles.active]: index === currentStep,
                                        [styles.done]: index < currentStep,
                                    })}
                                >
                                    <span className={styles.dot} />
                                    <span>{label}</span>
                                </div>
                            ))}
                        </div>

                        <p className={styles.autonote}>
                            <span className={styles.live} /> Your results will open here automatically — no need to do
                            anything.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default TrainingEvaluationInterstitial;
